import { create } from 'zustand';
import type { InboxItem, Cohort, MessageBody } from '../types/messaging';
import {
  getInbox,
  getThread,
  sendMessage as apiSendMessage,
  markRead,
  getUnreadCount,
  getMyCohorts,
} from '../api/messaging';

interface MessagingState {
  // Inbox state
  inboxItems: InboxItem[];
  inboxCursor: string | null;
  inboxLoading: boolean;
  inboxError: string | null;

  // Thread state
  activeThreadId: string | null;
  threadMessages: InboxItem[];
  threadLoading: boolean;

  // Unread count
  unreadCount: number;

  // Cohorts (for recipient picker)
  myCohorts: Cohort[];
  cohortsLoaded: boolean;

  // Actions
  loadInbox: (tenant: string, options?: { reset?: boolean; unreadOnly?: boolean }) => Promise<void>;
  loadMoreInbox: (tenant: string) => Promise<void>;
  openThread: (tenant: string, messageId: string) => Promise<void>;
  closeThread: () => void;
  sendMessage: (
    tenant: string,
    targetId: string,
    body: MessageBody,
    replyToId?: string
  ) => Promise<void>;
  markAsRead: (tenant: string, messageIds: string[]) => Promise<void>;
  refreshUnreadCount: (tenant: string) => Promise<void>;
  loadMyCohorts: (tenant: string) => Promise<void>;
  reset: () => void;
}

export const useMessagingStore = create<MessagingState>((set, get) => ({
  inboxItems: [],
  inboxCursor: null,
  inboxLoading: false,
  inboxError: null,

  activeThreadId: null,
  threadMessages: [],
  threadLoading: false,

  unreadCount: 0,

  myCohorts: [],
  cohortsLoaded: false,

  loadInbox: async (tenant, options = {}) => {
    const { reset = false, unreadOnly = false } = options;

    if (reset) {
      set({ inboxItems: [], inboxCursor: null });
    }

    set({ inboxLoading: true, inboxError: null });

    try {
      const response = await getInbox(tenant, { limit: 30, unreadOnly });
      set({
        inboxItems: response.items,
        inboxCursor: response.nextCursor,
        unreadCount: response.unreadCount,
        inboxLoading: false,
      });
    } catch (error) {
      set({
        inboxError: error instanceof Error ? error.message : 'Failed to load inbox',
        inboxLoading: false,
      });
    }
  },

  loadMoreInbox: async (tenant) => {
    const { inboxCursor, inboxItems, inboxLoading } = get();
    if (!inboxCursor || inboxLoading) return;

    set({ inboxLoading: true });

    try {
      const response = await getInbox(tenant, { limit: 30, cursor: inboxCursor });
      set({
        inboxItems: [...inboxItems, ...response.items],
        inboxCursor: response.nextCursor,
        inboxLoading: false,
      });
    } catch (error) {
      set({ inboxLoading: false });
    }
  },

  openThread: async (tenant, messageId) => {
    set({ activeThreadId: messageId, threadLoading: true, threadMessages: [] });

    try {
      const response = await getThread(tenant, messageId);
      set({
        threadMessages: response.messages,
        threadLoading: false,
      });

      // Update inbox items to mark as read
      const { inboxItems } = get();
      const readIds = response.messages.map((m) => m.message_id);
      set({
        inboxItems: inboxItems.map((item) =>
          readIds.includes(item.message_id) ? { ...item, is_read: true } : item
        ),
      });

      // Refresh unread count
      get().refreshUnreadCount(tenant);
    } catch (error) {
      set({ threadLoading: false });
    }
  },

  closeThread: () => {
    set({ activeThreadId: null, threadMessages: [] });
  },

  sendMessage: async (tenant, targetId, body, replyToId) => {
    const response = await apiSendMessage(tenant, { targetId, body, replyToId });

    // If we're in a thread, add the message to the thread
    const { activeThreadId, threadMessages } = get();
    if (activeThreadId && (replyToId || targetId === activeThreadId)) {
      // Reload the thread to get the new message with full details
      await get().openThread(tenant, activeThreadId);
    }

    // Reload inbox to show the new message
    await get().loadInbox(tenant, { reset: true });
  },

  markAsRead: async (tenant, messageIds) => {
    await markRead(tenant, messageIds);

    // Update local state
    const { inboxItems } = get();
    set({
      inboxItems: inboxItems.map((item) =>
        messageIds.includes(item.message_id) ? { ...item, is_read: true } : item
      ),
    });

    // Refresh unread count
    get().refreshUnreadCount(tenant);
  },

  refreshUnreadCount: async (tenant) => {
    try {
      const response = await getUnreadCount(tenant);
      set({ unreadCount: response.count });
    } catch (error) {
      // Ignore errors for unread count refresh
    }
  },

  loadMyCohorts: async (tenant) => {
    if (get().cohortsLoaded) return;

    try {
      const response = await getMyCohorts(tenant);
      set({ myCohorts: response.cohorts, cohortsLoaded: true });
    } catch (error) {
      // Ignore errors for cohorts load
    }
  },

  reset: () => {
    set({
      inboxItems: [],
      inboxCursor: null,
      inboxLoading: false,
      inboxError: null,
      activeThreadId: null,
      threadMessages: [],
      threadLoading: false,
      unreadCount: 0,
      myCohorts: [],
      cohortsLoaded: false,
    });
  },
}));
